import React, { ReactNode, useState, useEffect } from "react";
import Header from "@cloudscape-design/components/header";

import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Flashbar,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import title from "process";
import { Link } from "react-router-dom";
import { ListTenant } from "../data/data";
import { generateClient } from "aws-amplify/api";
import {
  createTenant,
  deleteTenant,
  updateTenant,
} from "../../../../graphql/mutations";
import { fetchAuthSession } from "aws-amplify/auth";

const client = generateClient();

// get user belong to what group and make it into string
async function checkUserGroup() {
  try {
    const { tokens } = await fetchAuthSession();
    // Normalize user groups to be an array of strings
    const groups = tokens?.accessToken.payload["cognito:groups"];
    if (Array.isArray(groups)) {
      return groups as string[]; // Safe to cast as string[]
    } else if (typeof groups === "string") {
      return [groups]; // Convert a single string to an array
    } else {
      return []; // Default to an empty array if not an array or string
    }
  } catch (error) {
    console.error("Error fetching user groups:", error);
    return []; // Default to an empty array in case of an error
  }
}

async function checkCurrentUser(): Promise<{
  username: string;
  email: string | null;
}> {
  try {
    const { tokens } = await fetchAuthSession();

    // Extract the user's information from the accessToken or idToken
    const currentUser =
      tokens?.idToken?.payload || tokens?.accessToken?.payload;

    if (currentUser) {
      const username =
        typeof currentUser["cognito:username"] === "string"
          ? currentUser["cognito:username"]
          : ""; // Fallback to an empty string if username is not a string

      // Check if email is a string, otherwise default to null
      const email =
        typeof currentUser.email === "string" ? currentUser.email : null; // Fallback to null if email is not a string

      return { username, email }; // Return user info with valid types
    } else {
      console.error("No user information found.");
      return { username: "", email: null }; // Return empty values if no user data
    }
  } catch (error) {
    console.error("Error fetching current user information:", error);
    return { username: "", email: null }; // Default to empty values in case of error
  }
}

interface FlashbarItem {
  type: "info" | "warning" | "error" | "success"; // Adjust according to your requirements
  dismissible: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  content: React.ReactNode;
  id: string;
}

async function createTenantApi(
  tenantname: string,
  subtenant: string,
  address: string,
  contact: string,
  phone: string,
  statusenable: string,
  servicepackage: string,
  status: boolean
) {
  try {
    await client.graphql({
      query: createTenant,
      authMode: "userPool",
      variables: {
        input: {
          tenantname: tenantname,
          subtenant: subtenant,
          address: address,
          contact: contact,
          phone: phone,
          status: statusenable,
          servicepackage: servicepackage,
          statuspayment: status,
        },
      },
    });
  } catch (err) {
    console.log(err);
  }
}

async function deleteTenantApi(id: string) {
  try {
    await client.graphql({
      query: deleteTenant,
      authMode: "userPool",
      variables: {
        input: {
          id,
        },
      },
    });
    // Optionally, refresh the list after deletion
    // fetchOverview();
  } catch (err) {
    console.log("Error deleting tenant : ", err);
  }
}

async function updateTenantApi(
  id: string,
  tenantname: string,
  subtenant: string,
  address: string,
  contact: string,
  phone: string,
  statusenable: string,
  servicepackage: string,
  status: boolean
) {
  try {
    await client.graphql({
      query: updateTenant,
      authMode: "userPool",
      variables: {
        input: {
          id: id,
          tenantname: tenantname,
          subtenant: subtenant,
          address: address,
          contact: contact,
          phone: phone,
          status: statusenable,
          servicepackage: servicepackage,
          statuspayment: status,
        },
      },
    });
  } catch (err) {
    console.log("Error updating vehicle: ", err);
  }
}

interface CellProps {
  StatusPayment: boolean;
}

const PaymentStatus: React.FC<CellProps> = ({ StatusPayment }) => {
  let statusType: "success" | "error" = "error"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (StatusPayment === true) {
    statusType = "success";
  } else if (StatusPayment === false) {
    statusType = "error";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Paid"
        : statusType === "error"
          ? "Unpaid"
          : "Unknown Status"}
    </StatusIndicator>
  );
};

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  ListTenant: ListTenant[];
  refreshData: () => void;
}

const columnDefinitions: TableProps<ListTenant>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    width: 70,
  },
  {
    header: "Tenant Name",
    cell: ({ TenantName }) => (
      <Link to={`/system/tenants/${TenantName}`}>{TenantName}</Link>
    ),
    sortingField: "Name",
    width: 100,
  },
  {
    header: "Subtenant",
    cell: ({ Subtenant }) => Subtenant,
    sortingField: "Subtenant",
    width: 100,
  },
  {
    header: "Address",
    cell: ({ Address }) => Address,
    sortingField: "Name",
    maxWidth: 100,
  },
  {
    header: "Contact",
    cell: ({ Contact }) => Contact,
    sortingField: "Contact",
    maxWidth: 150,
  },
  {
    header: "Phone",
    cell: ({ Phone }) => Phone,
    sortingField: "Position",
    maxWidth: 100,
  },
  {
    header: "Status",
    cell: ({ Status }) => Status,
    sortingField: "Status",
    maxWidth: 100,
  },
  {
    header: "Service Package",
    cell: ({ ServicePacakge }) => ServicePacakge,
    sortingField: "ServicePackage",
    maxWidth: 100,
  },
  {
    header: "Status Payment",
    cell: PaymentStatus,
    sortingField: "StatusPayment",
    maxWidth: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly ListTenant[] = [],
  selectedItems: readonly ListTenant[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VariationTable({
  ListTenant,
  refreshData,
}: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const [visible, setVisible] = React.useState(false);
  const [visible2, setVisible2] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  //const handleCancelClick = () => { setVisible(false) };
  const [isModalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [subtenantName, setsubtenantName] = useState("");
  const [tenantName, settenantName] = useState("");
  const [tenantAddress, settenantAddress] = useState("");
  const [tenantContact, settenantContact] = useState("");
  const [tenantPhone, settenantPhone] = useState("");
  const [tenantStatusenable, settenantStatusenable] = useState("");
  const [tenantServicePackage, settenantServicePackage] = useState("");
  const [tenantStatusPayment, settenantStatusPayment] = useState<
    boolean | null
  >(null);
  const [itemsFlashbar, setItemsFlashbar] = useState<FlashbarItem[]>([]);
  const [selectedTenantId, setselectedTenantId] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string>("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const timestamp = new Date().toISOString();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function initialize() {
      const groups = await checkUserGroup();
      const user = await checkCurrentUser();
      setCurrentUser(user.username);
      setCurrentUserEmail(user.email ?? "");
      setUserGroups(groups);
      setChecked(tenantStatusPayment === true);
    }
    initialize();
  }, [tenantStatusPayment]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const tenstatus =
        tenantStatusPayment !== null ? tenantStatusPayment : false;

      if (selectedTenantId) {
        await updateTenantApi(
          selectedTenantId,
          tenantName,
          subtenantName,
          tenantAddress,
          tenantContact,
          tenantPhone,
          tenantStatusenable,
          tenantServicePackage,
          tenstatus
        );
        // Log Transaction of update tenant
        console.log(
          `Tenant Transaction Log (Update Item):
        Tenant ID : ${selectedTenantId}
        Tenant Name : ${tenantName}
        Tenant Subtenant : ${subtenantName}
        Tenant Address : ${tenantAddress}
        Tenant Contact : ${tenantContact}
        Tenant Phone : ${tenantPhone}
        Tenant Status : ${tenantStatusenable}
        Tenant Service Package : ${tenantServicePackage}
        Tenant Status Payment : ${tenstatus}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );

        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "Tenant successfully updated.",
            id: "message_3",
          },
        ]);
      } else {
        await createTenantApi(
          tenantName,
          subtenantName,
          tenantAddress,
          tenantContact,
          tenantPhone,
          tenantStatusenable,
          tenantServicePackage,
          tenstatus
        );
        // Log Transaction of create tenant
        console.log(
          `Tenant Transaction Log (Add/Create Item):
        Tenant ID : ${selectedTenantId}
        Tenant Name : ${tenantName}
        Tenant Subtenant : ${subtenantName}
        Tenant Address : ${tenantAddress}
        Tenant Contact : ${tenantContact}
        Tenant Phone : ${tenantPhone}
        Tenant Status : ${tenantStatusenable}
        Tenant Service Package : ${tenantServicePackage}
        Tenant Status Payment : ${tenstatus}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );

        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "Tenant successfully created.",
            id: "message_2",
          },
        ]);
      }
      refreshData();
      setVisible(false);
      setVisible2(false);
      clearFormState();
    } catch (error) {
      console.error("Error Updating/Creating Tenants", error);
      setItemsFlashbar([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "an error occured. Please try again",
          id: "message_error",
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const selectedItem = collectionProps.selectedItems?.[0];
    if (selectedItem) {
      await deleteTenantApi(selectedItem.id);
      setModalDeleteVisible(false);
      // Log Transaction of delete tenant
      console.log(
        `Tenant Transaction Log (Delete Item):
        Tenant ID : ${selectedItem.id}
        Tenant Name : ${selectedItem.TenantName}
        Tenant Subtennat : ${selectedItem.Subtenant}
        Tenant Address : ${selectedItem.Address}
        Tenant Contact : ${selectedItem.Contact}
        Tenant Phone : ${selectedItem.Phone}
        Tenant Status : ${selectedItem.Status}
        Tenant Service Package : ${selectedItem.ServicePacakge}
        Tenant Status Payment: ${selectedItem.StatusPayment}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
      );

      refreshData();
      setItemsFlashbar([
        {
          type: "warning",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "Tenant successfully deleted !",
          id: "message_1",
        },
      ]);
    }
  };

  const handleDeleteCOnfirmation = () => {
    setModalDeleteVisible(true);
  };

  const clearFormState = () => {
    settenantName("");
    setsubtenantName("");
    settenantAddress("");
    settenantContact("");
    settenantPhone("");
    settenantStatusenable("");
    settenantServicePackage("");
    settenantStatusPayment(null);
    setChecked(false);
    setselectedTenantId(null);
  };

  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<ListTenant>(ListTenant, {
    filtering: {
      filteringFunction: (item, filteringText) => {
        const filterTextLower = filteringText.toLowerCase();
        const statusText = item.StatusPayment ? "paid" : "unpaid";
        if (statusText === filterTextLower) {
          return true;
        }
        return (
          item.id.toLowerCase().includes(filterTextLower) ||
          item.TenantName.toLowerCase().includes(filterTextLower) ||
          item.Subtenant.toLowerCase().includes(filterTextLower) ||
          item.Address.toLowerCase().includes(filterTextLower) ||
          item.Contact.toLowerCase().includes(filterTextLower) ||
          item.Phone.toLowerCase().includes(filterTextLower) ||
          item.Status.toLowerCase().includes(filterTextLower) ||
          item.ServicePacakge.toLowerCase().includes(filterTextLower)
        );
      },

      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      <Modal
        onDismiss={() => setModalDeleteVisible(false)}
        visible={isModalDeleteVisible}
        closeAriaLabel='Close modal'
        header='Confirm Delete'
        footer={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => setModalDeleteVisible(false)}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleDelete}>
              Confirm
            </Button>
          </SpaceBetween>
        }
      >
        <p>Are you sure you want to delete this Tenant ?</p>
      </Modal>
      <Modal
        onDismiss={() => {
          setVisible(false);
          clearFormState();
        }}
        visible={visible}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !tenantName ||
                  !subtenantName ||
                  !tenantAddress ||
                  !tenantContact ||
                  !tenantPhone ||
                  !tenantStatusenable ||
                  !tenantServicePackage
                }
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Add Tenant'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Tenant Name'>
              <Input
                value={tenantName}
                onChange={({ detail }) => settenantName(detail.value)}
              />
            </FormField>
            <FormField label='Subtenant'>
              <Input
                value={subtenantName}
                onChange={({ detail }) => setsubtenantName(detail.value)}
              />
            </FormField>
            <FormField label='Address'>
              <Input
                value={tenantAddress}
                onChange={({ detail }) => settenantAddress(detail.value)}
              />
            </FormField>
            <FormField label='Contact'>
              <Input
                value={tenantContact}
                onChange={({ detail }) => settenantContact(detail.value)}
              />
            </FormField>
            <FormField label='Phone'>
              <Input
                value={tenantPhone}
                onChange={({ detail }) => settenantPhone(detail.value)}
              />
            </FormField>
            <FormField label='Status'>
              <Input
                value={tenantStatusenable}
                onChange={({ detail }) => settenantStatusenable(detail.value)}
              />
            </FormField>
            {/*<FormField label="Service Package">
              <Select
                options={[
                  { label: "Gold", value: "1" },
                  { label: "Silver", value: "2" },
                  { label: "Basic", value: "3" },
                ]}
                selectedOption={null}
              />
            </FormField>*/}
            <FormField label='Service Package'>
              <Input
                value={tenantServicePackage}
                onChange={({ detail }) => settenantServicePackage(detail.value)}
              />
            </FormField>

            <FormField label='Status Payment'>
              <Checkbox
                onChange={({ detail }) => {
                  setChecked(detail.checked);
                  settenantStatusPayment(detail.checked);
                }}
                checked={checked}
              >
                Paid
              </Checkbox>
            </FormField>
          </SpaceBetween>
        </Container>
      </Modal>
      <Modal
        onDismiss={() => {
          setVisible2(false);
          clearFormState();
        }}
        visible={visible2}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible2(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button variant='primary' onClick={handleSubmit}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Edit Tenant'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Tenant Name'>
              <Input
                value={tenantName}
                onChange={({ detail }) => settenantName(detail.value)}
              />
            </FormField>
            <FormField label='Subtenant'>
              <Input
                value={subtenantName}
                onChange={({ detail }) => setsubtenantName(detail.value)}
              />
            </FormField>
            <FormField label='Address'>
              <Input
                value={tenantAddress}
                onChange={({ detail }) => settenantAddress(detail.value)}
              />
            </FormField>
            <FormField label='Contact'>
              <Input
                value={tenantContact}
                onChange={({ detail }) => settenantContact(detail.value)}
              />
            </FormField>
            <FormField label='Phone'>
              <Input
                value={tenantPhone}
                onChange={({ detail }) => settenantPhone(detail.value)}
              />
            </FormField>
            <FormField label='Status'>
              <Input
                value={tenantStatusenable}
                onChange={({ detail }) => settenantStatusenable(detail.value)}
              />
            </FormField>
            {/*<FormField label="Service Package">
              <Select
                options={[
                  { label: "Gold", value: "1" },
                  { label: "Silver", value: "2" },
                  { label: "Basic", value: "3" },
                ]}
                selectedOption={null}
              />
            </FormField>*/}
            <FormField label='Service Package'>
              <Input
                value={tenantServicePackage}
                onChange={({ detail }) => settenantServicePackage(detail.value)}
              />
            </FormField>

            <FormField label='Status Payment'>
              <Checkbox
                onChange={({ detail }) => {
                  setChecked(detail.checked);
                  settenantStatusPayment(detail.checked);
                }}
                checked={checked}
              >
                Paid
              </Checkbox>
            </FormField>
          </SpaceBetween>
        </Container>
      </Modal>
      <Flashbar items={itemsFlashbar} />

      <Table<ListTenant>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Tenant table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              ListTenant,
              collectionProps.selectedItems ?? [],
              items.length
            )}
            actions={
              <SpaceBetween size='s' direction='horizontal'>
                {/* <Button
                  disabled={collectionProps.selectedItems?.length === 0}
                  onClick={() => setVisible(true)}
                >
                  Edit
                </Button> */}
                <Button
                  disabled={(collectionProps.selectedItems ?? []).length === 0}
                  onClick={handleDeleteCOnfirmation}
                >
                  Delete
                </Button>

                <Button
                  disabled={(collectionProps.selectedItems ?? []).length === 0}
                  onClick={() => {
                    const selectedItem = collectionProps.selectedItems?.[0];
                    if (selectedItem) {
                      settenantName(selectedItem.TenantName);
                      setsubtenantName(selectedItem.Subtenant);
                      settenantAddress(selectedItem.Address);
                      settenantContact(selectedItem.Contact);
                      settenantPhone(selectedItem.Phone);
                      settenantStatusenable(selectedItem.Status);
                      settenantServicePackage(selectedItem.ServicePacakge);
                      settenantStatusPayment(selectedItem.StatusPayment);
                      setselectedTenantId(selectedItem.id);
                      setVisible2(true);
                    }
                  }}
                  variant='primary'
                >
                  Update
                </Button>
                <Button onClick={() => setVisible(true)} variant='primary'>
                  Add Tenant
                </Button>
              </SpaceBetween>
            }
          >
            Tenants
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Tenant'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        selectionType='single'
      />
    </>
  );
}
